import type { FeatureCollection, Polygon } from "geojson";

const AUTH_REMOTE_BASE_URL = (
  import.meta.env.VITE_AUTH_REMOTE_URL || "https://runwayapi.cityoptima.com"
).replace(/\/$/, "");
export const authApiBaseUrl = (
  import.meta.env.VITE_AUTH_BASE_URL ||
  (import.meta.env.DEV ? "/auth-api" : AUTH_REMOTE_BASE_URL)
).replace(/\/$/, "");
const AUTH_BASE_URL = authApiBaseUrl;
const TOKEN_STORAGE_KEY = "serviceToken";
const USER_STORAGE_KEY = "serviceUser";
const ADMIN_AIRPORT_STORAGE_KEY = "serviceAdminAirportSelection";
const EXPIRED_TOKEN_PATTERNS = [
  "invalid or expired token",
  "expired token",
  "token expired",
  "jwt expired",
  "missing or invalid auth token",
  "invalid service token",
];
let authRedirectInFlight = false;

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  token?: string | null;
  body?: unknown;
  signal?: AbortSignal;
};

export type PositionOption = {
  _id: string;
  key: string;
  label: string;
  description?: string;
};

export type AirportRecord = {
  _id: string;
  name: string;
  code: string;
  flightAirportCode?: string;
  flightAirportCodeType?: "auto" | "iata" | "icao";
  address?: string;
  city?: string;
  country?: string;
  timezone?: string | null;
  isActive?: boolean;
};

export type AirportSetupFile = {
  kind: AirportSetupFileKind;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  summary?: Record<string, unknown> | null;
  uploadedAt?: string | null;
  downloadUrl: string;
};

export type AirportSetupFloor = {
  floorId: string;
  label: string;
  level?: number | null;
  order: number;
  color: string;
  geoJsonFile?: AirportSetupFile | null;
};

export type AirportMapConfiguration = {
  center: { lat: number | null; lng: number | null };
  bounds: {
    west: number | null;
    south: number | null;
    east: number | null;
    north: number | null;
  };
  boundary?: Polygon | null;
  defaultZoom: number;
  defaultBearing: number;
  defaultPitch: number;
  floors: AirportSetupFloor[];
};

export type AirportSetupReadinessSection = {
  ready: boolean;
  missing: string[];
  optional?: boolean;
  crowdParametersConfigured?: boolean;
};

export type AirportSetup = {
  _id?: string | null;
  airport: string;
  mapConfiguration: AirportMapConfiguration;
  intelligenceConfiguration: {
    navigationGraph?: AirportSetupFile | null;
    poiGraph?: AirportSetupFile | null;
    crowdParameters?: AirportSetupFile | null;
    deteriorationParameters?: AirportSetupFile | null;
  };
  readiness: {
    basic: AirportSetupReadinessSection;
    map: AirportSetupReadinessSection;
    crowdPrediction: AirportSetupReadinessSection;
    deterioration: AirportSetupReadinessSection;
    airportCanBeSaved: boolean;
  };
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AirportRuntimeMap = {
  airport: Pick<AirportRecord, "_id" | "name" | "code" | "timezone">;
  mapConfiguration: AirportMapConfiguration;
  poiGraph?: AirportSetupFile | null;
  readiness: AirportSetupReadinessSection;
  updatedAt?: string | null;
};

export type AirportSetupFileKind =
  | "navigation_graph"
  | "poi_graph"
  | "crowd_parameters"
  | "deterioration_parameters"
  | "floor_geojson";

export type AirportGraphValidation = {
  valid: boolean;
  errors: string[];
  warnings: string[];
  summary: {
    totalFeatures: number;
    points: number;
    lines: number;
    polygons: number;
    outlines: number;
    navigationCorridors: number;
    crowdPois: number;
    unclassified: number;
    floors: string[];
  };
};

export type AirportGraphDraft = {
  _id: string;
  airport: string;
  schemaVersion: number;
  status: "draft" | "validated";
  sourceName: string;
  geoJson: FeatureCollection;
  validation: AirportGraphValidation;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AuthUserRecord = {
  _id: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  employee_id: string;
  email?: string | null;
  position?: PositionOption | string | null;
  airport?: AirportRecord | string | null;
  airports?: Array<AirportRecord | string> | null;
  uiSettings?: { language?: MobileLanguage | null } | null;
};

export type MobileLanguage =
  | "en"
  | "pa"
  | "fil"
  | "pt"
  | "fr"
  | "es"
  | "hi"
  | "ur"
  | "bn"
  | "ta";

export type WorkerRecord = {
  _id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  employee_id: string;
  email?: string | null;
  gender?: "male" | "female" | null;
  duty_class?: DutyClassInput;
  handle_gender_task?: "male" | "female" | "both" | null;
  has_previlage?: boolean | null;
  position?: PositionOption | string | null;
  airport?: AirportRecord | string | null;
  lines?: Array<
    | string
    | {
        _id?: string;
        key?: string;
        label?: string;
        name?: string;
      }
  > | null;
  uiSettings?: { language?: MobileLanguage | null } | null;
};

export type DutyClassRef = {
  _id?: string;
  key?: string;
  label?: string;
};

export type DutyClassValue = string;
export type DutyClassInput =
  | DutyClassValue[]
  | DutyClassValue
  | DutyClassRef[]
  | DutyClassRef
  | "ld/hd"
  | null;

export type WorkAreaAssignmentInput = {
  work_area: string;
  shift_definition?: string | null;
};

export type RegisterRequest = {
  firstName: string;
  lastName: string;
  preferredName: string;
  employee_id: string;
  email?: string | null;
  password: string;
  gender?: "male" | "female" | null;
  duty_class?: DutyClassInput;
  handle_gender_task?: "male" | "female" | "both";
  work_areas?: WorkAreaAssignmentInput[] | null;
  lines?: string[] | null;
  has_previlage?: boolean;
  position?: string;
  airportId?: string | null;
};

export type LoginRequest = {
  employee_id: string;
  password: string;
};

export type AirportUpsertRequest = {
  name?: string;
  code?: string;
  flightAirportCode?: string;
  flightAirportCodeType?: "auto" | "iata" | "icao";
  address?: string;
  city?: string;
  country?: string;
  timezone?: string;
  isActive?: boolean;
};

export type UserUpdateRequest = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  employee_id?: string;
  gender?: "male" | "female" | null;
  duty_class?: DutyClassInput;
  handle_gender_task?: "male" | "female" | "both";
  work_areas?: WorkAreaAssignmentInput[] | null;
  lines?: string[] | null;
  has_previlage?: boolean;
  email?: string;
  password?: string;
  position?: string;
  airportId?: string | null;
  airportIds?: string[];
  uiSettings?: { language: MobileLanguage };
};

type ApiErrorPayload = {
  message?: string;
  error?: string;
};

type AuthFailureOptions = {
  hadAuthToken?: boolean;
};

type HealthResponse = { status: string };
type PositionsResponse = { positions: PositionOption[] };
type AuthResponse = {
  token: string;
  expiresIn?: string;
  user: AuthUserRecord;
  message?: string;
};
type AirportSelectionResponse = {
  token: string;
  expiresIn?: string | number;
  scope?: "airport" | "general";
  airport: AirportRecord | null;
  message?: string;
};
type AirportsResponse = { airports: AirportRecord[] };
type AirportResponse = { airport: AirportRecord; message?: string };
type AirportSetupResponse = { setup: AirportSetup; message?: string };
type UserResponse = { user: AuthUserRecord; message?: string };
type WorkersResponse = { count: number; workers: WorkerRecord[] };
type DeleteResponse = { message?: string };

function getToken(explicitToken?: string | null) {
  if (explicitToken !== undefined) return explicitToken;
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function resolveApiErrorMessage(
  payload?: ApiErrorPayload | null,
  fallback = "Request failed",
) {
  return payload?.message || payload?.error || fallback;
}

export function shouldRedirectToLoginForAuthFailure(
  response?: Pick<Response, "status" | "statusText"> | null,
  payload?: ApiErrorPayload | null,
  { hadAuthToken = false }: AuthFailureOptions = {},
) {
  const normalizedMessage = resolveApiErrorMessage(
    payload,
    response?.statusText || "",
  )
    .trim()
    .toLowerCase();

  if (
    EXPIRED_TOKEN_PATTERNS.some((pattern) =>
      normalizedMessage.includes(pattern),
    )
  ) {
    return true;
  }

  return Number(response?.status || 0) === 401 && hadAuthToken;
}

export function redirectToLoginForAuthFailure() {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/login")) return;
  if (authRedirectInFlight) return;

  authRedirectInFlight = true;

  try {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(USER_STORAGE_KEY);
    window.localStorage.removeItem(ADMIN_AIRPORT_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup errors and continue redirecting to login.
  }

  window.location.assign("/login");
}

export function handleApiAuthFailure(
  response?: Pick<Response, "status" | "statusText"> | null,
  payload?: ApiErrorPayload | null,
  options?: AuthFailureOptions,
) {
  if (!shouldRedirectToLoginForAuthFailure(response, payload, options)) {
    return false;
  }

  redirectToLoginForAuthFailure();
  return true;
}

async function request<T>(
  path: string,
  { method = "GET", token, body, signal }: RequestOptions = {},
) {
  const resolvedToken = getToken(token);
  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (resolvedToken) {
    headers.Authorization = `Bearer ${resolvedToken}`;
  }

  const response = await fetch(`${AUTH_BASE_URL}${path}`, {
    method,
    signal,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const raw = await response.text();
  let parsed: unknown = null;

  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const payload = (parsed || {}) as ApiErrorPayload;
    handleApiAuthFailure(response, payload, {
      hadAuthToken: Boolean(resolvedToken),
    });
    throw new Error(resolveApiErrorMessage(payload, response.statusText));
  }

  return (parsed || {}) as T;
}

export async function healthCheck() {
  return request<HealthResponse>("/health");
}

export async function getPositions(
  options: Pick<RequestOptions, "signal"> = {},
) {
  const response = await request<PositionsResponse>("/api/positions", {
    signal: options.signal,
  });
  return response.positions || [];
}

export async function registerUser(payload: RegisterRequest) {
  return request<AuthResponse>("/api/auth/register", {
    method: "POST",
    body: payload,
  });
}

export async function loginUser(payload: LoginRequest) {
  return request<AuthResponse>("/api/auth/login", {
    method: "POST",
    body: payload,
  });
}

export async function selectSessionAirport(airportId: string) {
  return request<AirportSelectionResponse>("/api/auth/select-airport", {
    method: "POST",
    body: { airportId },
  });
}

export async function selectGeneralAdminSession() {
  return request<AirportSelectionResponse>("/api/auth/select-airport", {
    method: "POST",
    body: { scope: "general" },
  });
}

export async function logoutUser() {
  return request<DeleteResponse>("/api/auth/logout", {
    method: "POST",
  });
}

export async function listAirports(
  token?: string | null,
  options: Pick<RequestOptions, "signal"> = {},
) {
  const response = await request<AirportsResponse>("/api/airports", {
    token,
    signal: options.signal,
  });
  return response.airports || [];
}

export async function createAirport(
  payload: AirportUpsertRequest,
  token?: string | null,
) {
  const response = await request<AirportResponse>("/api/airports", {
    method: "POST",
    token,
    body: payload,
  });
  return response.airport;
}

export async function updateAirport(
  airportId: string,
  payload: AirportUpsertRequest,
  token?: string | null,
) {
  const response = await request<AirportResponse>(
    `/api/airports/${airportId}`,
    {
      method: "PUT",
      token,
      body: payload,
    },
  );
  return response.airport;
}

export async function getAirportSetup(
  airportId: string,
  token?: string | null,
) {
  const response = await request<AirportSetupResponse>(
    `/api/airports/${airportId}/setup`,
    { token },
  );
  return response.setup;
}

export async function getAirportRuntimeMap(
  airportId: string,
  token?: string | null,
) {
  const response = await request<{ runtimeMap: AirportRuntimeMap }>(
    `/api/airports/${airportId}/runtime-map`,
    { token },
  );
  return response.runtimeMap;
}

export async function getAirportSetupJsonFile(
  file: Pick<AirportSetupFile, "downloadUrl">,
  token?: string | null,
) {
  const downloadUrl = String(file?.downloadUrl || "").trim();
  if (!downloadUrl.startsWith("/api/")) {
    throw new Error("Airport setup file has an invalid download URL");
  }
  return request<unknown>(downloadUrl, { token });
}

export async function saveAirportSetup(
  airportId: string,
  mapConfiguration: AirportMapConfiguration,
  token?: string | null,
) {
  const response = await request<AirportSetupResponse>(
    `/api/airports/${airportId}/setup`,
    {
      method: "PUT",
      token,
      body: { mapConfiguration },
    },
  );
  return response.setup;
}

export async function uploadAirportSetupFile(
  airportId: string,
  kind: AirportSetupFileKind,
  payload: {
    filename: string;
    contentType: string;
    contentBase64: string;
    floorId?: string;
  },
  token?: string | null,
) {
  const response = await request<AirportSetupResponse>(
    `/api/airports/${airportId}/setup/files/${kind}`,
    {
      method: "PUT",
      token,
      body: payload,
    },
  );
  return response.setup;
}

export async function getAirportGraphDraft(
  airportId: string,
  token?: string | null,
) {
  const response = await request<{ draft: AirportGraphDraft | null }>(
    `/api/airports/${airportId}/graph-draft`,
    { token },
  );
  return response.draft;
}

export async function saveAirportGraphDraft(
  airportId: string,
  payload: { sourceName?: string; geoJson: FeatureCollection },
  token?: string | null,
) {
  const response = await request<{
    message: string;
    draft: AirportGraphDraft;
  }>(`/api/airports/${airportId}/graph-draft`, {
    method: "PUT",
    token,
    body: payload,
  });
  return response;
}

export async function deleteAirportGraphDraft(
  airportId: string,
  token?: string | null,
) {
  return request<{ message: string }>(
    `/api/airports/${airportId}/graph-draft`,
    { method: "DELETE", token },
  );
}

export async function updateUser(
  userId: string,
  payload: UserUpdateRequest,
  token?: string | null,
) {
  const response = await request<UserResponse>(`/api/users/${userId}`, {
    method: "PUT",
    token,
    body: payload,
  });
  return response.user;
}

// Existing read endpoint also covers the signed-in account outside the selected airport.
export async function readDirectoryUser(userId: string) {
  const users = await readDirectoryUsers([userId]);
  return users.find((user) => user._id === userId);
}

export async function readDirectoryUsers(
  userIds: string[],
  options?: { signal?: AbortSignal },
) {
  if (!userIds.length) return [];
  const response = await request<{ users: WorkerRecord[] }>(
    "/api/users/by-ids",
    {
      method: "POST",
      body: { userIds },
      signal: options?.signal,
    },
  );
  return response.users || [];
}

export async function updateUserStaffFields(
  userId: string,
  payload: Pick<UserUpdateRequest, "lines" | "has_previlage">,
) {
  return request(`/api/users/${userId}/staff-fields`, {
    method: "PUT",
    body: payload,
  });
}

export async function deleteUser(userId: string, token?: string | null) {
  return request<DeleteResponse>(`/api/users/${userId}`, {
    method: "DELETE",
    token,
  });
}

export async function listWorkersByAirportAndPosition(
  airportId: string,
  position: string,
  token?: string | null,
) {
  const query = new URLSearchParams({ airportId, position }).toString();
  const response = await request<WorkersResponse>(
    `/api/users/workers?${query}`,
    { token },
  );
  return response.workers || [];
}
